<template>
    <v-row dense>
        <v-col cols="12">
            <p class="mb-2">{{ $t('sub_event_search_info') }}</p>
        </v-col>
        <v-col cols="12" lg="6">
            <!-- search list -->
            <BaseRemoteList
                ref="searchList"
                height="500px"
                dense
                color="var(--tgk-grey)"
                session-key="event-sub-events-search-list"
                :collection-class="EventCollection"
                :extra-params="searchQueryParams"
                :filters="searchFilters"
                :headers="searchHeaders"
                :options="{ sortBy: ['next_schedule_date', 'title'], sortDesc: [true, false] }"
                hide-default-footer
                hide-top-toolbar
                :new-btn="false"
                :reload-btn="false"
                disable-pagination
                @loading="loadingSearch = $event"
            >
                <template #toolbar-start>
                    <h4>{{ $t('sub_event_search_title_short') }}</h4>
                </template>
                <template #item.next_schedule_date="{ item }">
                    <span>{{ item.next_schedule_date.formatIf('DD.MM.YYYY') }}</span>
                </template>
                <template #[`item.addSubEventButton`]="{ item }">
                    <ActionBtn
                        icon
                        x-small
                        :tooltip="$t('assign')"
                        :loading="loadingSubEvents"
                        @click="addSubEvent(item)"
                    >
                        <KpIcon icon-cls="arrow-bigright" color="action" />
                    </ActionBtn>
                </template>
            </BaseRemoteList>
        </v-col>
        <v-col cols="12" lg="6">
            <!-- assigned list -->
            <BaseList
                dense
                color="var(--tgk-grey)"
                session-key="event-sub-events-assigned-list"
                :loading="loadingSubEvents"
                :items="subEventCollection.models"
                :headers="assignedHeaders"
                hide-default-footer
                hide-top-toolbar
                :reload-btn="false"
                :new-btn="false"
                :query-field="false"
                :confirm-dialog-title="$t('delete_link')"
                :confirm-dialog-message="$t('delete_link_msg')"
                disable-pagination
                @edit-item="onEdit"
                @delete-item="removeSubEvent"
                @reload="loadSubEvents()"
            >
                <template #toolbar-start>
                    <h4>{{ $t('sub_event_assigned_title') }}</h4>
                </template>
                <template #item.next_schedule_date="{ item }">
                    <span>{{ item.next_schedule_date_display }}</span>
                </template>
            </BaseList>
        </v-col>
    </v-row>
</template>

<script>
import sessionStorageMixin from '@/mixins/session-storage';
import { errToStr } from '@/helpers/tools';
import { Event, EventCollection } from '@/models/Event';
import { instance as api } from '@/helpers/api';
import ActionBtn from '@/components/buttons/ActionBtn.vue';

export default {
    name: 'EventEditSubEvents',
    components: {
        ActionBtn,
    },
    mixins: [sessionStorageMixin],

    props: {
        record: {
            type: Event,
            required: true,
        },
    },

    data() {
        const subEventCollection = new EventCollection();

        return {
            EventCollection,
            loadingSubEvents: false,
            loadingSearch: false,
            showErwSuche: false,
            subEventCollection,
            assignedItemToDelete: null,
            searchHeaders: [
                { text: this.$t('titel'), sortable: true, value: '$.title', width: 170 },
                { text: this.$t('event_organizer'), sortable: true, value: '$.event_organizer', width: 150 },
                { text: this.$t('date'), sortable: true, value: 'next_schedule_date', width: 80 },
                { sortable: false, value: 'addSubEventButton', width: 50 },
            ],
            assignedHeaders: [
                { text: this.$t('titel'), sortable: true, value: '$.title', width: 170 },
                { text: this.$t('event_organizer'), sortable: true, value: '$.event_organizer', width: 150 },
                {
                    text: this.$t('date'),
                    sortable: true,
                    value: 'next_schedule_date',
                    width: 80,
                },
                { sortable: false, value: 'actions', width: 70 },
            ],
        };
    },

    computed: {
        searchQueryParams() {
            return {
                only_next: true,
                exclude_sub_events_for_id: this.record?.id,
            };
        },
        searchFilters() {
            return [
                {
                    property: 'id',
                    operator: 'NEQ', // NEQ = <> but wont be encoded
                    value: this.record?.id,
                },
            ];
        },
    },

    beforeCreate() {
        this.sessionKey = 'event-sub-events';
    },

    async mounted() {
        this.$emit('input', true);
        this.loadSubEvents();
    },

    methods: {
        async loadSubEvents() {
            this.loadingSubEvents = true;
            this.subEventCollection.addExtraParam('btn_all', true);
            this.subEventCollection.addExtraParam('parent_event_id', this.record ? this.record?.getId() : -1);
            await this.subEventCollection.query();
            this.loadingSubEvents = false;
        },

        async addSubEvent(event) {
            if (event) {
                try {
                    this.loadingSubEvents = true;
                    await api().post('/SubEvent/post', {
                        parent_event_id: this.record?.getId(),
                        child_event_id: event?.getId(),
                    });
                    await this.loadSubEvents();
                    this.$refs.searchList.reload();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.loadingSubEvents = false;
                }
            }
        },

        onEdit(subEvent) {
            this.$router.push({
                name: 'event-compact-edit',
                params: { id: subEvent?.id },
            });
        },

        async removeSubEvent(event) {
            if (event) {
                try {
                    this.loadingSubEvents = true;
                    await api().post('/SubEvent/removeLink', {
                        parent_id: this.record?.id,
                        child_id: event?.getId(),
                    });
                    await this.loadSubEvents();
                    this.$refs.searchList.reload();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.loadingSubEvents = false;
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: grid;
    grid-template-areas: 'search assigned';
    grid-template-rows: auto;
    grid-template-columns: 1fr 1fr;
}
.search-panel {
    grid-area: search;
}
.assigned-panel {
    grid-area: assigned;
    background-color: var(--toolbar-bg-color);
}
</style>
